import React, { useState } from "react";
import TagSelector from "../TagSelector.jsx";
import MetadataEditor from "../MetadataEditor.jsx";
import Chip from "../common/Chip.jsx";
import EmptyState from "../common/EmptyState.jsx";
import { unionNames, folderIncludeState } from "./importHelpers";
import { listPresets, savePreset } from "../../utils/importPresets";
import { findSidecarFiles, parseSidecarText } from "../../utils/sidecarParse";
import { notify } from "../../notifications/errorBus";

const PRESET_META_KEYS = ["authors", "sources", "license"];
const ARRAY_META = ["authors", "sources", "fileGroups"]; // mirrors importTree mergeMeta

/** Merge incoming metadata into target: array fields union, scalars override. */
function mergeMeta(target, incoming) {
    const out = { ...(target || {}) };
    for (const [k, v] of Object.entries(incoming || {})) {
        if (ARRAY_META.includes(k)) out[k] = unionNames(out[k], Array.isArray(v) ? v : [v]);
        else if (v !== undefined && v !== "") out[k] = v;
    }
    return out;
}

// Folders & files only carry the inheritable/editable fields here — no `name`
// (structural) and no server-owned keys.
//
// No `projects`: it was a free-text list retired by the D1 cutover, and
// `importTree`'s inheritable fields dropped it at the same time — so typing a
// project name here wrote a field nothing reads and didn't even inherit down
// the tree. Membership is set after import, from the file details modal or the
// browser's bulk action.
const INSPECTOR_SCHEMA = {
    authors: { type: "string[]", label: "Authors" },
    sources: { type: "string[]", label: "Sources" },
    license: {
        type: "string",
        label: "License",
        options: ["Owned", "CC0", "CC BY", "CC BY-NC", "CC BY-NC-SA", "CC BY-ND", "CC BY-SA"],
    },
};

/**
 * Right-hand editor for the selected tree node (folder or file). Edits mutate
 * the node in place and call `onChange()` to re-render. Tags are stored as
 * NAMES (the backend resolves them to workspace tag IDs at import). Folder edits
 * apply recursively to everything inside via flatten-time inheritance.
 */
const ImportInspector = ({ selected, idToName, nameToId, onChange, disabled, workspaceId, onSelectRoot }) => {
    const [parsingSidecar, setParsingSidecar] = useState(false);

    if (!selected) {
        return (
            <div className="iw-inspector iw-inspector-empty">
                <EmptyState
                    compact
                    title="Nothing selected"
                    description="Drop or choose a folder to begin."
                />
            </div>
        );
    }

    const { node, isFolder, inheritedTags } = selected;
    const isRoot = !!selected.isRoot || node.path === "";
    const displayName = isRoot ? "All files" : node.name;

    const addTag = (name) => {
        const clean = String(name || "").trim();
        if (!clean) return;
        node.tags = unionNames(node.tags, [clean]);
        onChange();
    };
    const removeTag = (name) => {
        node.tags = (node.tags || []).filter((t) => t !== name);
        onChange();
    };

    // AI suggestions live on node.suggestedTags (transient). Accepting promotes
    // one to a real tag; the user can also dismiss or accept all.
    const acceptSuggested = (name) => {
        node.tags = unionNames(node.tags, [name]);
        node.suggestedTags = (node.suggestedTags || []).filter((t) => t !== name);
        onChange();
    };
    const dismissSuggested = (name) => {
        node.suggestedTags = (node.suggestedTags || []).filter((t) => t !== name);
        onChange();
    };
    const acceptAllSuggested = () => {
        node.tags = unionNames(node.tags, node.suggestedTags || []);
        node.suggestedTags = [];
        onChange();
    };

    // Presets (saved author/source/license/tags bundles) + sidecar parsing.
    const presets = listPresets(workspaceId);
    const sidecars = isFolder ? findSidecarFiles(node) : [];

    const applyPreset = (preset) => {
        if (!preset) return;
        node.tags = unionNames(node.tags, preset.tags || []);
        node.metadata = mergeMeta(node.metadata, preset.metadata || {});
        onChange();
        notify({ type: "success", title: "Preset applied", message: `Applied "${preset.name}".` });
    };
    const saveAsPreset = () => {
        const name = window.prompt("Save current tags + author/source/license as a preset named:");
        if (!name || !name.trim()) return;
        const metadata = {};
        for (const k of PRESET_META_KEYS) {
            if (node.metadata && node.metadata[k] !== undefined) metadata[k] = node.metadata[k];
        }
        savePreset(workspaceId, name, { tags: node.tags || [], metadata });
        onChange();
        notify({ type: "success", title: "Preset saved", message: `Saved preset "${name.trim()}".` });
    };
    const parseSidecar = async () => {
        if (sidecars.length === 0) return;
        setParsingSidecar(true);
        try {
            let merged = {};
            const filled = new Set();
            for (const sc of sidecars) {
                try {
                    // eslint-disable-next-line no-await-in-loop -- a handful of small text files
                    const parsed = parseSidecarText(await sc.file.text());
                    Object.keys(parsed).forEach((k) => filled.add(k));
                    merged = mergeMeta(merged, parsed);
                } catch {
                    /* skip an unreadable sidecar */
                }
            }
            if (filled.size === 0) {
                notify({ type: "info", title: "Nothing found", message: "Couldn't read author/license from the sidecar file(s)." });
                return;
            }
            node.metadata = mergeMeta(node.metadata, merged);
            onChange();
            notify({
                type: "success",
                title: "Sidecar parsed",
                message: `Filled: ${[...filled].join(", ")}. Review under Metadata below.`,
            });
        } finally {
            setParsingSidecar(false);
        }
    };

    const saveMeta = (key, value) => {
        node.metadata = { ...(node.metadata || {}), [key]: value };
        onChange();
        return Promise.resolve();
    };
    const removeMeta = (key) => {
        const next = { ...(node.metadata || {}) };
        delete next[key];
        node.metadata = next;
        onChange();
        return Promise.resolve();
    };

    // Map this node's own tag names to IDs so TagSelector hides already-added
    // existing tags; freshly-typed names that aren't real tags yet are simply
    // not excluded (they're not in the dropdown anyway).
    const ownTagIds = (node.tags || [])
        .map((n) => nameToId[n.toLowerCase()])
        .filter(Boolean);

    const includeState = isFolder ? folderIncludeState(node) : node.include !== false ? "all" : "none";

    return (
        <div className="iw-inspector">
            <div className="iw-inspector-head">
                <span className="iw-inspector-icon">{isRoot ? "🗂️" : isFolder ? "📁" : "📄"}</span>
                <span className="iw-inspector-title" title={isRoot ? "Everything in this import" : node.path || node.name}>
                    {displayName}
                </span>
                {!isRoot && onSelectRoot && (
                    <button type="button" className="iw-text-btn iw-whole-import" onClick={onSelectRoot}>
                        Whole import
                    </button>
                )}
            </div>

            {isFolder && (
                <p className="iw-hint">
                    {isRoot
                        ? "Tags & metadata here apply to the entire import."
                        : "Tags & metadata here apply to every file inside this folder."}
                </p>
            )}

            <label className="iw-include-toggle">
                <input
                    type="checkbox"
                    checked={includeState !== "none"}
                    ref={(el) => {
                        if (el) el.indeterminate = includeState === "some";
                    }}
                    disabled={disabled}
                    onChange={(e) => selected.onToggleInclude(e.target.checked)}
                />
                {isRoot ? "Include all files" : isFolder ? "Include all files in this folder" : "Include this file"}
            </label>

            {!disabled && (
                <div className="iw-quickfill">
                    <select
                        className="iw-preset-select"
                        value=""
                        disabled={presets.length === 0}
                        onChange={(e) => {
                            if (e.target.value !== "") applyPreset(presets[Number(e.target.value)]);
                        }}
                        aria-label="Apply a preset"
                    >
                        <option value="">{presets.length ? "Apply preset…" : "No presets yet"}</option>
                        {presets.map((p, i) => (
                            <option key={p.name} value={i}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                    <button type="button" className="iw-text-btn" onClick={saveAsPreset}>
                        Save as preset
                    </button>
                    {sidecars.length > 0 && (
                        <button type="button" className="iw-text-btn" onClick={parseSidecar} disabled={parsingSidecar}>
                            {parsingSidecar
                                ? "Reading…"
                                : sidecars.length === 1
                                ? `📄 Parse ${sidecars[0].name}`
                                : `📄 Parse ${sidecars.length} sidecars`}
                        </button>
                    )}
                </div>
            )}

            <div className="iw-section">
                <h4>Tags</h4>
                {inheritedTags && inheritedTags.length > 0 && (
                    <div className="iw-inherited">
                        <span className="iw-inherited-label">Inherited:</span>
                        {inheritedTags.map((t) => (
                            <Chip
                                key={t}
                                size="sm"
                                className="iw-chip-inherited"
                                title="Inherited from a parent folder"
                            >
                                {t}
                            </Chip>
                        ))}
                    </div>
                )}
                <div className="iw-own-tags">
                    {(node.tags || []).map((t) => (
                        <Chip
                            key={t}
                            size="sm"
                            onRemove={disabled ? undefined : () => removeTag(t)}
                            removeLabel={`Remove tag ${t}`}
                        >
                            {t}
                        </Chip>
                    ))}
                    {(node.tags || []).length === 0 && <span className="iw-muted">No own tags yet.</span>}
                </div>
                {(node.suggestedTags || []).length > 0 && (
                    <div className="iw-suggested">
                        <div className="iw-suggested-head">
                            <span className="iw-suggested-label">✨ AI suggested</span>
                            {!disabled && (
                                <button type="button" className="iw-text-btn" onClick={acceptAllSuggested}>
                                    Accept all
                                </button>
                            )}
                        </div>
                        {node.aiDescription && <p className="iw-ai-desc">{node.aiDescription}</p>}
                        <div className="iw-own-tags">
                            {node.suggestedTags.map((t) => (
                                <Chip
                                    key={t}
                                    size="sm"
                                    variant="accent-subtle"
                                    className="iw-chip-suggested"
                                    onRemove={disabled ? undefined : () => dismissSuggested(t)}
                                    removeLabel={`Dismiss suggested tag ${t}`}
                                >
                                    {t}
                                    <button
                                        type="button"
                                        className="iw-tag-accept"
                                        disabled={disabled}
                                        onClick={() => acceptSuggested(t)}
                                        aria-label={`Accept suggested tag ${t}`}
                                        title="Accept"
                                    >
                                        +
                                    </button>
                                </Chip>
                            ))}
                        </div>
                    </div>
                )}
                {!disabled && (
                    <TagSelector
                        initialTags={ownTagIds}
                        onExistingTagAdded={(id) => addTag(idToName[id] || "")}
                        onNewTagAdded={(name) => addTag(name)}
                    />
                )}
            </div>

            <div className="iw-section">
                <h4>Metadata</h4>
                <MetadataEditor
                    metadata={node.metadata || {}}
                    schema={INSPECTOR_SCHEMA}
                    onSave={saveMeta}
                    onRemove={removeMeta}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default ImportInspector;
